import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { LabExam } from './LabExam';
import { getApiV1Url } from './config/api';
import { formatDate } from './utils/dateUtils';
import { useUser } from './contexts/UserContext';

const buildShareLink = (lab: LabExam | null) => {
    if (!lab || !lab.id || typeof window === 'undefined') {
        return '';
    }
    return `${window.location.origin}/lab-exam/${lab.id}`;
};



const SectionRow: React.FC<{ label: string; value?: string }> = ({ label, value }) => {
    if (!value) return null;
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{
                fontSize: 11,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                color: '#0f172a',
                opacity: 0.65,
                fontWeight: 600,
            }}>
                {label}
            </span>
            <span style={{
                fontSize: 16,
                color: '#0f172a',
                fontWeight: 500,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                lineHeight: 1.45,
            }}>
                {value}
            </span>
        </div>
    );
};



const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <div style={{
        background: '#fff',
        border: '1px solid rgba(0,113,188,0.08)',
        borderRadius: 18,
        padding: 28,
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        boxShadow: '0 22px 50px rgba(0,113,188,0.08)',
    }}>
        <h3 style={{ margin: 0, fontSize: 16, color: '#111827' }}>{title}</h3>
        <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
            {children}
        </div>
    </div>
);

const LabExamDetailPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { examId } = useParams<{ examId: string }>();

    const parsedId = useMemo(() => {
        if (!examId) return null;
        const numeric = Number(examId);
        return Number.isNaN(numeric) ? null : numeric;
    }, [examId]);

    const initialLabState = (location.state as { lab?: LabExam } | undefined)?.lab;
    const initialLab = initialLabState && parsedId && initialLabState.id === parsedId ? initialLabState : null;
    const [lab, setLab] = useState<LabExam | null>(initialLab);
    const [loading, setLoading] = useState(!initialLab);
    const [error, setError] = useState<string | null>(null);
    const [copyStatus, setCopyStatus] = useState<'idle' | 'copied' | 'error'>('idle');
    const [toggleLoading, setToggleLoading] = useState(false);

    const { user } = useUser();
    const isEditor = !!user?.groups?.some((g) => g.name === 'Редактор');

    useEffect(() => {
        if (!parsedId) {
            setError('Некорректный идентификатор испытания.');
            setLoading(false);
            return;
        }

        if (lab) return;

        let cancelled = false;
        const fetchLab = async () => {
            setLoading(true);
            setError(null);
            try {
                const access = localStorage.getItem('access');
                const activeUrl = getApiV1Url('LAB_EXAMS_LIST');
                const deletedUrl = `${activeUrl}deleted/`;

                const response = await axios.get<LabExam[]>(activeUrl, {
                    headers: access ? { Authorization: `Bearer ${access}` } : undefined,
                });
                if (cancelled) return;

                let found = (response.data || []).find(item => item.id === parsedId);

                // Если запись удалена, она лежит в отдельном endpoint.
                if (!found) {
                    const deletedResponse = await axios.get<LabExam[]>(deletedUrl, {
                        headers: access ? { Authorization: `Bearer ${access}` } : undefined,
                    });
                    found = (deletedResponse.data || []).find(item => item.id === parsedId);
                }

                if (found) setLab(found);
                else setError('Испытание не найдено. Возможно, данные были обновлены.');
            } catch (err) {
                if (!cancelled) {
                    setError('Ошибка загрузки данных об испытании.');
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        fetchLab();
        return () => { cancelled = true; };
    }, [lab, parsedId]);

    const shareLink = useMemo(() => buildShareLink(lab), [lab]);

    const handleCopyLink = async () => {
        if (!shareLink) return;
        try {
            await navigator.clipboard.writeText(shareLink);
            setCopyStatus('copied');
            setTimeout(() => setCopyStatus('idle'), 2000);
        } catch {
            setCopyStatus('error');
            setTimeout(() => setCopyStatus('idle'), 2000);
        }
    };

    const handleBack = () => {
        if (location.key !== 'default') {
            navigate(-1);
        } else {
            navigate('/lab-exam');
        }
    };

    const handleToggleDelete = async () => {
        if (!parsedId || !lab) return;
        if (!isEditor) return;

        const isDeleted = !!lab.status;
        const nextStatus = !isDeleted; // false -> удалить (true), true -> восстановить (false)
        const actionLabel = isDeleted ? 'восстановить' : 'удалить';

        const confirmed = window.confirm(`Вы уверены, что хотите ${actionLabel} запись?`);
        if (!confirmed) return;

        try {
            setToggleLoading(true);
            setError(null);

            const access = localStorage.getItem('access');
            const url = `${getApiV1Url('LAB_EXAMS_LIST')}status/${parsedId}/toggle/`;

            await axios.patch(
                url,
                { status: nextStatus },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        ...(access ? { Authorization: `Bearer ${access}` } : {}),
                    },
                },
            );

            navigate(nextStatus ? '/lab-exam/deleted' : '/lab-exam');
        } catch (e) {
            setError('Ошибка при изменении статуса записи.');
        } finally {
            setToggleLoading(false);
        }
    };
    useEffect(() => {
        document.title = 'Лабораторные испытания — АПТС';
    }, []);

    if (loading) {
        return (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '60vh',
            }}>
                Загрузка данных испытания...
            </div>
        );
    }

    if (error || !lab) {
        return (
            <div style={{ padding: 24 }}>
                <div style={{
                    background: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: 8,
                    padding: 24,
                    maxWidth: 640,
                    margin: '0 auto',
                }}>
                    <h2 style={{ marginTop: 0 }}>Не удалось открыть испытание</h2>
                    <p style={{ color: '#4b5563', lineHeight: 1.5 }}>{error || 'Данные отсутствуют.'}</p>
                    <button
                        onClick={() => navigate('/lab-exam')}
                        style={{
                            padding: '10px 16px',
                            borderRadius: 6,
                            border: '1px solid #d1d5db',
                            background: '#fff',
                            cursor: 'pointer',
                        }}
                    >
                        К списку испытаний
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div style={{ padding: '32px 24px', display: 'flex', justifyContent: 'center', background: '#f3f7fb', minHeight: '100vh' }}>
            <div style={{ width: '100%', maxWidth: 1200, display: 'flex', flexDirection: 'column', gap: 24 }}>
                <div style={{
                    background: '#0071bc',
                    borderRadius: 16,
                    padding: 32,
                    color: '#fff',
                    boxShadow: '0 24px 50px rgba(0, 113, 188, 0.35)',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 16,
                    alignItems: 'center',
                }}>
                    <button
                        onClick={handleBack}
                        style={{
                            padding: '10px 20px',
                            borderRadius: 999,
                            border: '1px solid rgba(255,255,255,0.4)',
                            background: 'transparent',
                            color: '#fff',
                            cursor: 'pointer',
                            fontWeight: 500,
                        }}
                    >
                        ← Назад
                    </button>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1, minWidth: 260 }}>
                        <h1 style={{ margin: 0, fontSize: 28, fontWeight: 700 }}>Лабораторные испытания</h1>
                        <span style={{ fontSize: 16, opacity: 0.95 }}>
                            {lab.product_name || 'Испытание без названия'}
                        </span>
                        <span style={{ opacity: 0.9, fontSize: 14 }}>
                            Дата проведения: {lab.exam_date ? formatDate(lab.exam_date) : 'Не указана'}
                        </span>
                    </div>
                    {shareLink && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 220 }}>
                            <button
                                onClick={handleCopyLink}
                                style={{
                                    padding: '10px 18px',
                                    borderRadius: 999,
                                    border: 'none',
                                    background: '#fff',
                                    color: '#0071bc',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                }}
                            >
                                {copyStatus === 'copied'
                                    ? 'Ссылка скопирована'
                                    : copyStatus === 'error'
                                        ? 'Ошибка копирования'
                                        : 'Скопировать ссылку'}
                            </button>
                        </div>
                    )}
                    {isEditor && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 220 }}>
                            <button
                                onClick={handleToggleDelete}
                                disabled={toggleLoading}
                                style={{
                                    padding: '10px 18px',
                                    borderRadius: 999,
                                    border: lab.status
                                        ? '1px solid rgba(34,197,94,0.85)'
                                        : '1px solid rgba(239,68,68,0.85)',
                                    background: lab.status
                                        ? 'rgba(34,197,94,0.25)'
                                        : 'rgba(239,68,68,0.85)',
                                    color: '#fff',
                                    fontWeight: 700,
                                    cursor: toggleLoading ? 'not-allowed' : 'pointer',
                                }}
                            >
                                {toggleLoading
                                    ? 'Подождите...'
                                    : lab.status
                                        ? 'Восстановить'
                                        : 'Удалить'}
                            </button>
                        </div>
                    )}
                </div>

                <Section title="Производитель">
                    <SectionRow label="Округ" value={lab.state_company} />
                    <SectionRow label="Регион" value={lab.region_company} />
                    <SectionRow label="Наименование" value={lab.name_company} />
                    <SectionRow label="ИНН" value={lab.inn_company} />
                    <SectionRow label="Адрес" value={lab.address_company} />
                </Section>

                <Section title="Объект и результаты">
                    <SectionRow label="Наименование объекта" value={lab.object_name} />
                    <SectionRow label="Адрес объекта" value={lab.object_adres} />
                    <SectionRow label="Координаты" value={lab.geomaps} />
                    <SectionRow label="Причина обращения" value={lab.reason_request} />
                    <SectionRow label="Время эксплуатации" value={lab.exploitation_time} />
                    <SectionRow label="Клиент" value={lab.customer} />
                    <SectionRow label="Результат испытания" value={lab.exam_result} />
                    <SectionRow label="Тип сварного соединения" value={lab.welding_joint_type} />
                </Section>

                <Section title="Продукция и документы">
                    <SectionRow label="Продукция" value={lab.product_name} />
                    <SectionRow label="Техническая документация" value={lab.technical_documentation} />
                    <SectionRow label="Требования соответствия" value={lab.compliance_requirements} />
                </Section>
            </div>
        </div>
    );
};

export default LabExamDetailPage;

